/**
 * What the platform assumes on a new deal, as the form words it.
 *
 * Mirrors `backend/app/services/intake_defaults.py`: Senior where nothing says
 * otherwise (Whole Loan under `POSITION_DEFAULT`, Chris Young's 26 Oct 2026
 * ruling, held until the client signs), USD, and the measure the property type
 * is quoted in. Behind `ENTRY_DEFAULTS_V1` (off by default) also Midas,
 * Floating and Monthly.
 * The form never types these into a box — an unset select reads
 * "Senior (default)" (or "Whole Loan (default)") and the server records the assumption as a Suggestion
 * (`source = "default"`), so a value a person chose can always be told from
 * one the platform supplied.
 */
import {
  DEFAULT_CAPITAL_PROGRAM,
  DEFAULT_PAYMENT_FREQUENCY,
  DEFAULT_RATE_TYPE,
  ENTRY_DEFAULTS_ON,
  entryDefaultsV1,
} from './entryDefaultsV1'
import { defaultMeasureFor, measureDisplayLabel } from './measureDefaults'
import { defaultPosition } from './positionDefault'

/** Where a loan sits in the stack when nothing says otherwise, as shipped.
 *  Read `defaultPosition()` for the one in force under `POSITION_DEFAULT`. */
export const DEFAULT_POSITION = 'Senior'

/** The currency a deal's money is held in until a package or a person names one. */
export const DEFAULT_CURRENCY = 'USD'

/**
 * The text of an unset select's empty option. "—" for an ordinary field; for the
 * three the platform assumes, what it will assume — so the person sees the
 * default before they accept it, and picking anything else is the override.
 */
export function emptyOptionLabel(field: string, assetType?: string, options?: readonly string[]): string {
  if (field === 'position') return `${defaultPosition()} (default)`
  if (field === 'currency') return `${DEFAULT_CURRENCY} (default)`
  if (field === 'unit_of_measure') {
    const measure = defaultMeasureFor(assetType)
    return measure
      ? `${measureDisplayLabel(measure, assetType, options)} (default)`
      : '— (no default for this type)'
  }
  if (entryDefaultsV1() === ENTRY_DEFAULTS_ON) {
    if (field === 'capital_program') return `${DEFAULT_CAPITAL_PROGRAM} (default)`
    if (field === 'rate_type') return `${DEFAULT_RATE_TYPE} (default)`
    if (field === 'payment_frequency') return `${DEFAULT_PAYMENT_FREQUENCY} (default)`
  }
  return '—'
}

/** What an unset select will be assumed to be, or null when the platform assumes nothing. */
export function assumedValue(field: string, assetType?: string): string | null {
  if (field === 'position') return defaultPosition()
  if (field === 'currency') return DEFAULT_CURRENCY
  if (field === 'unit_of_measure') return defaultMeasureFor(assetType)
  if (entryDefaultsV1() === ENTRY_DEFAULTS_ON) {
    if (field === 'capital_program') return DEFAULT_CAPITAL_PROGRAM
    if (field === 'rate_type') return DEFAULT_RATE_TYPE
    if (field === 'payment_frequency') return DEFAULT_PAYMENT_FREQUENCY
  }
  return null
}
